import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { useUnit } from 'effector-react'
import { format, parseISO } from 'date-fns'

import { $transactinHistoryPagination, getTransactionHistoryFx, TransactionHistoryItemType } from '@/entities/payments'
import { $userToken } from '@/entities/user'
import { TransactionHistoryItem } from '@/shared/ui/dataWrappers/payments'
import { useStyles } from '../styles'
import { Skeleton } from '@mui/material'

type TransactionHistoryListProps = {
  history: TransactionHistoryItemType[]
}

export const TransactionHistoryList = (props: TransactionHistoryListProps) => {
  const { classes } = useStyles()
  const { history } = props

  const pagination = useUnit($transactinHistoryPagination)
  const userToken = useUnit($userToken)

  const [isFetching, setIsFetching] = useState(false)
  const triggerRef = useRef<HTMLDivElement | null>(null)

  const groupedTransactions = useMemo(() => {
    const groups: Record<string, typeof history> = {}

    history.forEach((item) => {
      const dateObj = new Date(item.created_at)
      const year = dateObj.getFullYear()
      const month = String(dateObj.getMonth() + 1).padStart(2, '0')
      const day = String(dateObj.getDate()).padStart(2, '0')
      const dateKey = `${year}-${month}-${day}`

      if (!groups[dateKey]) groups[dateKey] = []
      groups[dateKey].push(item)
    })

    return Object.entries(groups)
      .map(([date, transactions]) => ({
        date,
        transactions: transactions.sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
      }))
      .sort((a, b) => b.date.localeCompare(a.date))
  }, [history])

  const handleLoadMore = useCallback(async () => {
    if (!userToken || isFetching || !pagination) return
    if (pagination.page >= pagination.pages) return

    setIsFetching(true)
    await getTransactionHistoryFx({
      token: userToken,
      page: pagination.page + 1,
      size: pagination.size ?? 30,
    })
    setIsFetching(false)
  }, [isFetching, pagination, userToken])

  useEffect(() => {
    const target = triggerRef.current
    if (!target) return

    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !isFetching) {
          handleLoadMore()
        }
      },
      { rootMargin: '200px' }
    )

    observer.observe(target)

    return () => {
      if (target) observer.unobserve(target)
    }
  }, [pagination, isFetching, handleLoadMore])

  const flatTransactions = useMemo(
    () => groupedTransactions.flatMap((group) => group.transactions),
    [groupedTransactions]
  )

  const triggerId = flatTransactions.at(-3)?.transaction_id

  return (
    <div className={classes.groupedList}>
      {groupedTransactions.map((group) => (
        <div
          key={group.date}
          className={classes.list}
        >
          <div className={classes.dateText}>{format(parseISO(group.date), 'dd.MM.yy')}</div>
          {group.transactions.map((item) => (
            <div
              key={item.transaction_id}
              ref={item.transaction_id === triggerId ? triggerRef : null}
            >
              <TransactionHistoryItem {...item} />
            </div>
          ))}
        </div>
      ))}

      {isFetching && (
        <div
          className={classes.list}
          style={{ marginTop: '12px' }}
        >
          {Array.from({ length: 5 }).map((_, index) => (
            <Skeleton
              key={`skeleton-${index}`}
              height={80}
              style={{ width: '100%', borderRadius: '8px' }}
            />
          ))}
        </div>
      )}
    </div>
  )
}
